import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getUserDecks, Deck } from '../services/deckService';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  Snackbar,
  Avatar
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import RecommendIcon from '@mui/icons-material/Recommend';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TranslateIcon from '@mui/icons-material/Translate';
import { GB, RU, JP, CN, DE, FR } from 'country-flag-icons/react/3x2';

const UserPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, changeLanguage, isLoading: isLanguageLoading } = useLanguage();
  const [recentDecks, setRecentDecks] = useState<Deck[]>([]);
  const [recommendedDecks, setRecommendedDecks] = useState<Deck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [stats, setStats] = useState({
    totalDecks: 0,
    cardsStudied: 0,
    studySessions: 0,
    averageRating: 0,
  });

  // Language flags mapping
  const languageFlags: Record<string, React.ComponentType<{ className?: string }>> = {
    en: GB,
    ru: RU,
    ja: JP,
    zh: CN,
    de: DE,
    fr: FR
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          // Получаем колоды пользователя
          const decks = await getUserDecks(user.uid);
          
          // Сортируем по дате последнего изучения для получения недавних колод
          const sortedByRecent = [...decks].sort((a, b) => {
            const dateA = new Date(a.stats.lastStudied);
            const dateB = new Date(b.stats.lastStudied);
            return dateB.getTime() - dateA.getTime();
          });
          
          setRecentDecks(sortedByRecent.slice(0, 3));
          
          // Выбираем рекомендованные колоды (с наименьшим прогрессом)
          const sortedByProgress = [...decks].sort((a, b) => {
            const progressA = a.stats.cardsLearned / (a.stats.totalCards || 1);
            const progressB = b.stats.cardsLearned / (b.stats.totalCards || 1);
            return progressA - progressB;
          });
          
          setRecommendedDecks(sortedByProgress.slice(0, 3));
          
          // Подсчитываем статистику
          const totalCardsStudied = decks.reduce((sum, deck) => sum + (deck.stats.cardsStudied || 0), 0);
          const totalSessions = decks.reduce((sum, deck) => sum + (deck.stats.dailyProgress?.length || 0), 0);
          const avgRatings = decks.filter(deck => deck.stats.averageRating > 0).map(deck => deck.stats.averageRating);
          const avgRating = avgRatings.length > 0 
            ? avgRatings.reduce((sum, rating) => sum + rating, 0) / avgRatings.length
            : 0;
            
          setStats({
            totalDecks: decks.length,
            cardsStudied: totalCardsStudied,
            studySessions: totalSessions,
            averageRating: avgRating
          });
          
          setIsLoading(false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsLoading(false);
        }
      }
    };

    fetchUserData();
  }, [user]);

  if (!user) {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const handleLanguageChange = async (event: SelectChangeEvent) => {
    try {
      const selectedLanguage = event.target.value as 'en' | 'ru' | 'ja' | 'zh' | 'de' | 'fr';
      await changeLanguage(selectedLanguage);
      
      setSnackbar({
        open: true,
        message: t('messages.languageChanged'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error changing language:', error);
      setSnackbar({
        open: true,
        message: t('common.error'),
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-deck')}
          >
            {t('decks.createDeck')}
          </Button>
        </Box>

        {/* Языковые настройки */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TranslateIcon color="primary" /> 
            {t('profile.languageSettings')}
          </Typography>
          <Box sx={{ maxWidth: 300, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="language-select-label">{t('profile.selectLanguage')}</InputLabel>
              <Select
                labelId="language-select-label"
                id="language-select"
                value={language}
                label={t('profile.selectLanguage')}
                onChange={handleLanguageChange}
                disabled={isLanguageLoading}
              >
                <MenuItem value="en">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GB className="flag-icon" style={{ width: '20px', height: '15px' }} />
                    <span>{t('profile.languages.en')}</span>
                  </Box>
                </MenuItem>
                <MenuItem value="ru">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RU className="flag-icon" style={{ width: '20px', height: '15px' }} />
                    <span>{t('profile.languages.ru')}</span>
                  </Box>
                </MenuItem>
                <MenuItem value="ja">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <JP className="flag-icon" style={{ width: '20px', height: '15px' }} />
                    <span>{t('profile.languages.ja')}</span>
                  </Box>
                </MenuItem>
                <MenuItem value="zh">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CN className="flag-icon" style={{ width: '20px', height: '15px' }} />
                    <span>{t('profile.languages.zh')}</span>
                  </Box>
                </MenuItem>
                <MenuItem value="de">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DE className="flag-icon" style={{ width: '20px', height: '15px' }} />
                    <span>{t('profile.languages.de')}</span>
                  </Box>
                </MenuItem>
                <MenuItem value="fr">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FR className="flag-icon" style={{ width: '20px', height: '15px' }} />
                    <span>{t('profile.languages.fr')}</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Статистика */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChartIcon color="primary" /> 
            {t('profile.stats')}
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 2
          }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.totalDecks}</Typography>
              <Typography color="text.secondary">{t('decks.myDecks')}</Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.cardsStudied}</Typography>
              <Typography color="text.secondary">{t('cards.cardsStudied')}</Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.studySessions}</Typography>
              <Typography color="text.secondary">{t('study.sessions')}</Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.averageRating.toFixed(1)}</Typography>
              <Typography color="text.secondary">{t('stats.averageRating')}</Typography>
            </Paper>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 3, mb: { xs: 3, md: 0 }, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                {t('decks.recentlyStudied')}
              </Typography>
              
              {recentDecks.length > 0 ? (
                <List>
                  {recentDecks.map((deck) => (
                    <React.Fragment key={deck.id}>
                      <ListItem 
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => navigate(`/decks/${deck.id}`)}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            {deck.title.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={deck.title} 
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {deck.stats.cardsStudied} / {deck.stats.totalCards} {t('decks.cards')}
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={(deck.stats.cardsStudied / (deck.stats.totalCards || 1)) * 100}
                                sx={{ mt: 0.5, height: 5, borderRadius: 5 }}
                              />
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/study/${deck.id}`);
                            }}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', my: 4 }}>
                  {t('decks.noStudiedDecks')}
                </Typography>
              )}
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/decks')}
                  startIcon={<VisibilityIcon />}
                >
                  {t('decks.myDecks')}
                </Button>
              </Box>
            </Paper>
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RecommendIcon color="primary" />
                {t('decks.recommendedForStudy')}
              </Typography>
              
              {recommendedDecks.length > 0 ? (
                <List>
                  {recommendedDecks.map((deck) => (
                    <React.Fragment key={deck.id}>
                      <ListItem 
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => navigate(`/decks/${deck.id}`)}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'secondary.light' }}>
                            {deck.title.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={deck.title} 
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {deck.stats.cardsLearned} / {deck.stats.totalCards} {t('decks.learned')}
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={(deck.stats.cardsLearned / (deck.stats.totalCards || 1)) * 100}
                                sx={{ mt: 0.5, height: 5, borderRadius: 5 }}
                                color="secondary"
                              />
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            color="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/study/${deck.id}`);
                            }}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', my: 4 }}>
                  {t('decks.noRecommendedDecks')}
                </Typography>
              )}
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button 
                  variant="outlined" 
                  color="secondary"
                  onClick={() => navigate('/public-decks')}
                  startIcon={<VisibilityIcon />}
                >
                  {t('decks.publicDecks')}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserPage; 